import { useCallback, useRef, type Dispatch, type SetStateAction } from 'react'
import { useIntl } from 'react-intl'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import { patchCartItemStatus } from 'tripPlanner/api/cartStatus'
import { useTripPlannerEventBus } from 'tripPlanner/assistant/contexts/TripPlannerEventBus'
import { useItineraryContext } from '../contexts/ItineraryContext'
import type { CartPanelItem } from './useCartPanelItems'
import { buildCartItemInclusionToggledActivityEntry } from './buildCartItemInclusionToggledActivityEntry'

interface InclusionOperation {
  value: boolean
  status: 'pending' | 'succeeded' | 'failed'
}

interface InclusionItem {
  savedValue: boolean
  operations: Array<InclusionOperation>
}

interface InclusionSession {
  isCurrent: () => boolean
  items: Map<number, InclusionItem>
  tail: Promise<void>
}

// Mounted with the cart projection, not the panel. Optimistic state and its
// reconciliation must have the same lifetime, including while the cart is closed.
export function useCartInclusionMutation(
  chatId: number | undefined,
  setOptimisticExclusions: Dispatch<SetStateAction<Record<number, boolean>>>,
  refetchCartItems: () => Promise<Map<string, App.MultiCartItem>>,
) {
  const { captureCartOperationScope, refetchCurrentChatItinerary } = useItineraryContext()
  const eventBus = useTripPlannerEventBus()
  const intl = useIntl()
  const sessionRef = useRef<InclusionSession | undefined>(undefined)
  const errorMessage = intl.formatMessage({
    id: 'EmZPR-KxU7oRFczGeJk2',
    defaultMessage: 'Something went wrong. Please try again.',
    description: 'Default error message when a chat streaming request fails',
  })

  return useCallback((item: CartPanelItem): Promise<void> => {
    if (!chatId) return Promise.resolve()
    if (!sessionRef.current?.isCurrent()) {
      sessionRef.current = { isCurrent: captureCartOperationScope(), items: new Map(), tail: Promise.resolve() }
    }
    const session = sessionRef.current
    const itemIds = item.productType === 'flight' ? item.pairedTripItemIds : [item.tripItemId]
    const operation: InclusionOperation = { value: !item.cartExcluded, status: 'pending' }
    for (const itemId of itemIds) {
      const current = session.items.get(itemId) ?? { savedValue: item.cartExcluded, operations: [] }
      session.items.set(itemId, { ...current, operations: [...current.operations, operation] })
    }
    setOptimisticExclusions(current => ({ ...current, ...Object.fromEntries(itemIds.map(id => [id, operation.value])) }))

    // Preserve server write order as well as UI order, including overlapping
    // paired-flight item sets. A failed older choice cannot overwrite a newer one.
    const result = session.tail.then(async() => {
      if (!session.isCurrent()) return
      try {
        await patchCartItemStatus(chatId, itemIds.map(itemId => ({ itemId, cartExcluded: operation.value })))
        operation.status = 'succeeded'
      } catch {
        operation.status = 'failed'
      }
      if (!session.isCurrent()) return
      const settledValues: Record<number, boolean> = {}
      for (const itemId of itemIds) {
        const current = session.items.get(itemId)
        if (!current) continue
        const latestChoice = current.operations.filter(entry => entry.status !== 'failed').at(-1)
        settledValues[itemId] = latestChoice?.value ?? current.savedValue
        if (current.operations.every(entry => entry.status !== 'pending')) session.items.delete(itemId)
      }
      setOptimisticExclusions(current => ({ ...current, ...settledValues }))
      refetchCurrentChatItinerary()
      await refetchCartItems().catch(() => undefined)
      if (!session.isCurrent()) return
      if (operation.status === 'failed') {
        showSnackbar(errorMessage, 'critical')
        return
      }
      // Reporting follows reconciliation; it cannot skip rollback or keep a lock.
      eventBus.dispatch({
        type: 'trip_planner_surface_activity',
        entries: [buildCartItemInclusionToggledActivityEntry(item.name, operation.value)],
      })
    })
    session.tail = result.catch(() => undefined)
    return result
  }, [captureCartOperationScope, chatId, errorMessage, eventBus, refetchCartItems, refetchCurrentChatItinerary, setOptimisticExclusions])
}
